/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */

/*
 * ua.doc.email — the no-code email body editor control.
 *
 * Cloned from ua.doc.simple (Quill-based rich text) and extended into an ordered
 * BLOCK model. Each block maps 1:1 to a generic email.* element so the saved value
 * compiles directly into an email template:
 *
 *   text    -> email.text     (Quill rich text, email-safe inline tags only)
 *   list    -> email.list     (ordered = numbered, else dash bullets)
 *   button  -> email.button   (label + url)
 *   image   -> email.image    (uploaded asset; src/alt/width/href)
 *   divider -> email.divider  (layout)
 *   spacer  -> email.spacer   (layout)
 *
 * Same control contract as ua.doc.simple: control:true, preview:"ua.preview.json",
 * Quill build copy, save()/load(). Quill is constrained to the email-safe inline
 * format set (bold/italic/underline/link/list) so getSemanticHTML() can never emit
 * markup an email client would choke on.
 *
 * The saved value (repo.data) is the block array:
 *   [ { type:"text", html:"<p>..</p>" },
 *     { type:"list", ordered:true, items:["a","b"] },
 *     { type:"button", label:"..", url:".." },
 *     { type:"image", src:"..", alt:"..", width:480, href:".." },
 *     { type:"divider" }, { type:"spacer", height:24 } ]
 *
 * NOTE: the interactive editor DOM + Quill mounting run in the browser only. This
 * module is written to the same contract as ua.doc.simple; the live in-browser
 * editor/preview is verified once the app is served (npm start) — see the task
 * report's "What I could NOT verify."
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.doc.email",
  title: "Email Document",
  description: "A no-code email body editor: ordered blocks (text, list, button, image, divider, spacer) that compile to email.* elements.",
  control: true,
  preview: "ua.preview.json",
  prevalidate: true,
  supportServer: false,
  build: {
    copy: {
      from: "@!/node_modules/quill/dist/",
      to: "@!/app/build/quill/"
    }
  },
  fields: {
    "name": {
      title: "Name",
      type: "string",
      description: "The name of the control.",
      required: true
    },
    "title": {
      title: "Title",
      type: "string",
      description: "The title of the control.",
      required: true
    },
    "description": {
      title: "Description",
      type: "inner",
      description: "The help text or description shown above the editor."
    },
    "value": {
      title: "Value",
      type: ["string", "inner"],
      description: "The initial block array (or its JSON string) for the control."
    },
    "theme": {
      title: "Theme",
      type: "string",
      description: "The Quill theme used inside text blocks.",
      default: "snow",
      list: {
        must: ["snow", "bubble"]
      },
      addtouae: true
    },
    "uploadUrl": {
      title: "Image Upload URL",
      type: "string",
      description: "The endpoint an image block POSTs to; returns an absolute URL.",
      default: "/o/wizard/email-image",
      addtouae: true
    },
    "inputClass": {
      title: "Input Class",
      type: "string",
      description: "Extra class applied to the editor container."
    },
    "inputStyle": {
      title: "Input Style",
      type: "string",
      description: "Inline style applied to the editor container."
    },
    "required": {
      title: "Required",
      type: "boolean",
      description: "If at least one content block is required.",
      default: false,
      addtouae: true
    },
    "minlength": {
      title: "Minimum Blocks",
      type: "number",
      description: "Minimum number of content blocks.",
      default: 0,
      addtouae: true
    },
    "maxlength": {
      title: "Maximum Blocks",
      type: "number",
      description: "Maximum number of content blocks (0 = unlimited).",
      default: 0,
      addtouae: true
    }
  },
  passthrough: {
    except: ["inner", "type", "id", "inputclass", "inputstyle", "value"]
  },
  external: {
    name: "Quill Editor",
    src: "@!/build/quill/quill.js",
    type: "text/javascript"
  }
});

var events = require("../../../uam/events.js");

var jsonRender = require("../interface/jsonRender.js");
var uai = require("../interface/interface.js");

// The ONLY formats a text block's Quill instance may use. Constraining Quill here
// is what guarantees getSemanticHTML() emits email-safe inline markup only.
//   "token" — the atomic ${…} chip embed (M4 · EE3). It is COSMETIC: a chip always
//   serializes back to the LITERAL ${token} string, so the block model, the EE1
//   refuse-gate, and the send-time substitutor keep reading ${x}. A tenant that
//   authors no tokens never creates a chip, so behaviour is byte-identical for them.
var EMAIL_SAFE_FORMATS = ["bold", "italic", "underline", "link", "list", "token"];

// Block types this control understands.
//   LEAF      — a single email.* element (greeting added for platform templates).
//   CONTAINER — email.card / email.section: a SURFACE that HOLDS child blocks. A
//               container block carries a `children` array; its inner text becomes
//               editable child blocks (this is what lets a card-nested template be
//               edited without flattening away the card). The UI nests one level
//               (leaves inside a container); deeper data nesting still round-trips.
//   _raw      — an opaque, preserved structural node (email.header/uajson/footer, a
//               ${…}-token email.list). Shown as a locked row and re-emitted VERBATIM
//               so a load→save round-trip never drops it.
var LEAF_TYPES = ["text", "list", "button", "image", "divider", "spacer", "greeting"];
var CONTAINER_TYPES = ["card", "section"];
// The order block-type buttons appear in the top-level add bar.
var BLOCK_TYPES = LEAF_TYPES.concat(CONTAINER_TYPES);

// Is this a container (children-bearing) block type?
function isContainerType(type) { return CONTAINER_TYPES.indexOf(type) !== -1; }

var themes = {
  "snow":   { "css": "@!/build/quill/quill.snow.css",   "loaded": false },
  "bubble": { "css": "@!/build/quill/quill.bubble.css", "loaded": false }
};

/**
 * Render the control shell. The interactive block editor is built into this shell
 * by the interface.afterrender handler (browser only), mirroring ua.doc.simple.
 * The initial block value rides along as a JSON string data-attribute.
 * @param {*} options The element options.
 * @returns {object} A UA/JSON element.
 */
exports.render = async function (options) {

  var randomName = uai.generateRandomID(24);

  // Serialize the initial value to a JSON string for the client to hydrate from.
  var initial = "[]";
  try {
    if (typeof options.value === "string") {
      // Already a string; trust it is a JSON block array (fall back to []).
      var probe = options.value.trim();
      initial = probe.charAt(0) === "[" ? probe : "[]";
    } else if (Array.isArray(options.value)) {
      initial = JSON.stringify(options.value);
    } else if (options.value && typeof options.value === "object") {
      // A doc body { blocks:[...] } shape is accepted too.
      if (Array.isArray(options.value.blocks)) {
        initial = JSON.stringify(options.value.blocks);
      }
    }
  } catch (e) {
    initial = "[]";
  }

  var editor = {
    "n": "div",
    "id": randomName,
    "class": "ua-doc-email",
    "data-blocks": initial,
    "data-theme": ("theme" in options) ? options.theme : _.define.fields.theme.default,
    "data-upload": ("uploadUrl" in options) ? options.uploadUrl : _.define.fields.uploadUrl.default
  };

  try {
    editor = jsonRender.pass({ ...options }, { ...editor }, ["name", "class", "icon", "style"]);
  } catch (error) {
    // non-fatal — pass is a convenience for attribute forwarding.
  }

  if ("inputclass" in options) {
    editor.class += " " + options.inputclass;
  }
  if ("inputstyle" in options) {
    editor.style = options.inputstyle;
  }

  var element = {
    "n": "div",
    "name": options.name,
    "c": "form-group pb-3",
    "i": [{
      "n": "label",
      "for": randomName,
      "c": "pb-2 fs-4",
      "i": options.title
    }, {
      "n": "div",
      "c": "ua-doc-email-h",
      "i": editor
    }]
  };

  if ("description" in options) {
    element.i.push({
      "n": "div",
      "id": `${randomName}-description`,
      "c": "form-text",
      "i": options.description
    });
  }

  // Load the Quill theme CSS once (browser only).
  if (typeof document !== "undefined") {
    var theme = editor["data-theme"];
    if (theme in themes && !(themes[theme].loaded)) {
      var css = document.createElement("link");
      css.setAttribute("rel", "stylesheet");
      css.setAttribute("href", uai.resolveURL(themes[theme].css));
      document.head.appendChild(css);
      themes[theme].loaded = true;
    }
  }

  return element;
};

/**
 * Hold processing until Quill is available on the window (browser only).
 * @returns {Promise<boolean>}
 */
async function UntilQuillIsLoaded() {
  if (typeof Quill === "undefined") {
    await new Promise(function (resolve) { setTimeout(resolve, 100); });
    return UntilQuillIsLoaded();
  }
  return true;
}

/* ---------------------------------------------------------------- token chip (EE3)
 * A ${identifier} inside a text block's Quill renders as ONE atomic, non-editable
 * "chip": a single backspace deletes the whole token, the caret cannot land inside
 * it, and it can never be split or partially edited. The chip is COSMETIC ONLY — on
 * read-back it flattens to the literal ${token} string (html() below + the data-token
 * flatten in sanitizeEmailHtml), so readBlocks / collectBlockTokens / the EE1 refuse-
 * gate / the send-time substitutor all keep reading the literal ${x}. Browser only.
 */
var _tokenBlotRegistered = false;
function registerTokenBlot() {
  if (_tokenBlotRegistered) { return; }
  if (typeof Quill === "undefined") { return; }
  var Embed;
  try { Embed = Quill.import("blots/embed"); } catch (e) { return; }
  if (!Embed) { return; }

  // Quill's inline Embed base wraps the visible text in a contenteditable=false
  // content node flanked by cursor guards — that is what makes the chip select and
  // delete as a single unit and keeps the caret from entering it.
  class TokenChip extends Embed {
    static create(value) {
      var node = super.create();
      var token = String(value == null ? "" : value);
      node.setAttribute("data-token", token);
      node.textContent = token; // moved into the guarded content node by the base ctor
      return node;
    }
    static value(node) {
      return node.getAttribute("data-token") || "";
    }
    // getSemanticHTML() calls html() directly and does NOT escape its return, so a
    // chip serializes to the LITERAL ${token} — no <span> — byte-identical to the
    // plain-text source the block model held before EE3.
    html() {
      return this.domNode.getAttribute("data-token") || "";
    }
  }
  TokenChip.blotName = "token";
  TokenChip.tagName = "span";
  TokenChip.className = "egt-token-chip";

  Quill.register(TokenChip, true); // suppress the overwrite warning; idempotent via the flag
  _tokenBlotRegistered = true;
}

// Turn ${token} TEXT into chip embeds on paste AND on hydrate — dangerouslyPasteHTML
// runs the clipboard matchers, so loading an existing template's ${x} shows chips.
// Registered per Quill instance. Inline formatting on the surrounding text is
// preserved; the embed itself is atomic and never carries formatting.
function addTokenMatcher(quill) {
  if (typeof Quill === "undefined" || !quill || !quill.clipboard) { return; }
  var Delta;
  try { Delta = Quill.import("delta"); } catch (e) { return; }
  if (!Delta) { return; }
  quill.clipboard.addMatcher(3 /* Node.TEXT_NODE */, function (node, delta) {
    var text = (node && node.data) || "";
    if (text.indexOf("${") === -1) { return delta; }
    var re = /\$\{[A-Za-z0-9_]+\}/g;
    if (!re.test(text)) { return delta; }
    re.lastIndex = 0;
    // Preserve any inline formatting Quill computed for this whole text node.
    var attributes = (delta && delta.ops && delta.ops.length === 1 && delta.ops[0].attributes)
      ? delta.ops[0].attributes : null;
    var ops = [];
    var last = 0, m;
    function pushText(s) {
      if (!s) { return; }
      var op = { insert: s };
      if (attributes) { op.attributes = attributes; }
      ops.push(op);
    }
    while ((m = re.exec(text))) {
      pushText(text.slice(last, m.index));
      ops.push({ insert: { token: m[0] } });
      last = m.index + m[0].length;
    }
    pushText(text.slice(last));
    return new Delta(ops);
  });
}

/* ---------------------------------------------------------------- client helpers
 * These run in the browser. They are defined at module scope (not inside the
 * event handler) so they are easy to reason about; each is a pure DOM builder.
 */

// Escape a string for safe insertion into an HTML attribute / text node.
function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Strip a Quill semantic-HTML string down to the email-safe inline tag set.
// Quill is already format-constrained, but this is defense-in-depth: it removes
// any tag outside the allowlist and drops all attributes except href on <a>.
function sanitizeEmailHtml(html) {
  if (typeof document === "undefined") { return html; }
  var ALLOWED = { P: 1, BR: 1, B: 1, STRONG: 1, I: 1, EM: 1, U: 1, A: 1, OL: 1, UL: 1, LI: 1, SPAN: 1 };
  var tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  (function walk(node) {
    var children = Array.prototype.slice.call(node.childNodes);
    for (var i = 0; i < children.length; i++) {
      var c = children[i];
      if (c.nodeType === 1) {
        // A token chip flattens to its literal ${token}, whatever DOM it serialized
        // as. getSemanticHTML() already emits the literal via the blot's html(), so
        // this is defense-in-depth (raw chip DOM, pasted markup) — never the 14
        // platform templates, whose text carries no chip element.
        if (c.getAttribute && c.getAttribute("data-token") != null) {
          node.replaceChild(document.createTextNode(c.getAttribute("data-token") || ""), c);
          continue;
        }
        if (!ALLOWED[c.tagName]) {
          // Unwrap disallowed element: replace it with its children.
          while (c.firstChild) { node.insertBefore(c.firstChild, c); }
          node.removeChild(c);
          continue;
        }
        // Drop every attribute except href on <a>.
        var attrs = Array.prototype.slice.call(c.attributes);
        for (var a = 0; a < attrs.length; a++) {
          var name = attrs[a].name;
          if (!(c.tagName === "A" && name === "href")) {
            c.removeAttribute(name);
          }
        }
        walk(c);
      } else if (c.nodeType === 3) {
        // Strip any zero-width cursor-guard chars an embed may have left in a text
        // node so they never enter the block model.
        if (c.data && c.data.indexOf("\uFEFF") !== -1) { c.data = c.data.replace(/\uFEFF/g, ""); }
      }
    }
  })(tmp);
  return tmp.innerHTML;
}

// Build an add-block toolbar. `types` is the list of block types the bar offers
// (top-level offers all; a container's nested bar offers leaves only). `target` is
// the blocks container new rows are appended to.
function buildAddBar(container, addBlock, types, target) {
  var bar = document.createElement("div");
  bar.className = "ua-doc-email-addbar";
  (types || BLOCK_TYPES).forEach(function (type) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ua-doc-email-add btn btn-sm btn-outline-secondary";
    btn.setAttribute("data-add", type);
    btn.textContent = "+ " + type.charAt(0).toUpperCase() + type.slice(1);
    btn.addEventListener("click", function () { addBlock(type, null, target || null); });
    bar.appendChild(btn);
  });
  container.appendChild(bar);
  return bar;
}

/**
 * interface.afterrender — build the interactive editor into each ua.doc.email
 * shell (browser only), mount Quill per text block, wire add/reorder/delete, and
 * bubble a debounced change event on the uae placeholder so UATools autosaves.
 */
var isLoading = false;
events.on("interface.afterrender", async function () {

  if (typeof document === "undefined") { return; }
  if (isLoading) { return; }
  isLoading = true;

  try {
    var elements = document.querySelectorAll(`uae[namespace="${_.namespace}"]`);

    for (var i = 0; i < elements.length; i++) {
      let element = elements[i];
      if (element.uaDocEmail) { continue; } // already initialized

      await UntilQuillIsLoaded();
      registerTokenBlot(); // idempotent; must run before any `new Quill` uses the "token" format

      var host = element.querySelector(".ua-doc-email");
      if (!host) { continue; }

      var theme = host.getAttribute("data-theme") || _.define.fields.theme.default;
      var uploadUrl = host.getAttribute("data-upload") || _.define.fields.uploadUrl.default;

      // The live block state for this control instance.
      var state = { blocks: [], theme: theme, uploadUrl: uploadUrl, quills: [] };
      element.uaDocEmail = state;

      // A blocks container + an add bar.
      var blocksEl = document.createElement("div");
      blocksEl.className = "ua-doc-email-blocks";
      host.appendChild(blocksEl);

      // Debounced change bubble (mirrors ua.doc.simple's 1s debounce).
      var changeTimer = null;
      function bubbleChange() {
        clearTimeout(changeTimer);
        changeTimer = setTimeout(function () {
          element.dispatchEvent(new Event("change"));
        }, 1000);
      }

      // Re-index move-button disabled states after add/remove/reorder. Scoped to
      // DIRECT child rows of each blocks container (top-level and every container's
      // nested `.ua-doc-email-children`) so nesting never corrupts sibling ordering.
      function refreshControls() {
        var containers = [blocksEl];
        var nested = host.querySelectorAll(".ua-doc-email-children");
        for (var n = 0; n < nested.length; n++) { containers.push(nested[n]); }
        for (var c = 0; c < containers.length; c++) {
          var rows = [];
          var kids = containers[c].children;
          for (var k = 0; k < kids.length; k++) {
            if (kids[k].classList && kids[k].classList.contains("ua-doc-email-block")) { rows.push(kids[k]); }
          }
          for (var r = 0; r < rows.length; r++) {
            var up = rows[r].querySelector("[data-move='up']");
            var down = rows[r].querySelector("[data-move='down']");
            // A row's chrome buttons live in its own chrome; a NESTED row also matches
            // these selectors, so only touch the button whose closest block is this row.
            if (up && up.closest(".ua-doc-email-block") === rows[r]) { up.disabled = (r === 0); }
            if (down && down.closest(".ua-doc-email-block") === rows[r]) { down.disabled = (r === rows.length - 1); }
          }
        }
      }

      // Build one block row for a given type + optional seed data, appended to
      // `target` (defaults to the top-level blocks container). Containers append their
      // children to their own nested container.
      function addBlock(type, data, target) {
        if (BLOCK_TYPES.indexOf(type) === -1 && type !== "_raw") { return; }
        data = data || {};
        var parentEl = target || blocksEl;

        var row = document.createElement("div");
        row.className = "ua-doc-email-block";
        row.setAttribute("data-type", type);

        // Row chrome: move up / move down / delete.
        var chrome = document.createElement("div");
        chrome.className = "ua-doc-email-chrome";
        chrome.innerHTML =
          '<button type="button" class="btn btn-sm btn-outline-secondary" data-move="up" title="Move up">&#9650;</button>' +
          '<button type="button" class="btn btn-sm btn-outline-secondary" data-move="down" title="Move down">&#9660;</button>' +
          '<span class="ua-doc-email-type">' + esc(type) + '</span>' +
          '<button type="button" class="btn btn-sm btn-outline-danger" data-move="del" title="Delete">&times;</button>';
        row.appendChild(chrome);

        // Scope move/delete to THIS row's own chrome button (a nested container has its
        // own buttons that also match the selector).
        var upBtn = chrome.querySelector("[data-move='up']");
        var downBtn = chrome.querySelector("[data-move='down']");
        var delBtn = chrome.querySelector("[data-move='del']");
        upBtn.addEventListener("click", function () {
          var prev = row.previousElementSibling;
          if (prev) { row.parentNode.insertBefore(row, prev); refreshControls(); bubbleChange(); }
        });
        downBtn.addEventListener("click", function () {
          var next = row.nextElementSibling;
          if (next) { row.parentNode.insertBefore(next, row); refreshControls(); bubbleChange(); }
        });
        delBtn.addEventListener("click", function () {
          row.parentNode.removeChild(row); refreshControls(); bubbleChange();
        });

        var body = document.createElement("div");
        body.className = "ua-doc-email-body";
        row.appendChild(body);

        if (type === "_raw") {
          // Preserved structural node — locked, re-emitted verbatim. Store the node so
          // readBlocks can return it unchanged; show a read-only summary.
          row.uaRaw = data.node || null;
          var ns = (data.node && (data.node.namespace || data.node.n)) ? (data.node.namespace || data.node.n) : "structural";
          body.innerHTML =
            '<div class="ua-doc-email-raw text-muted small">Preserved region: <code>' + esc(ns) + '</code> (kept as-is on save)</div>';
          // A preserved region cannot be deleted from here (it is chrome/structure).
          delBtn.disabled = true;
        } else if (isContainerType(type)) {
          // CONTAINER: header inputs for its own attributes + a nested blocks area with
          // its own add bar. Children hold the editable inner blocks.
          if (type === "card") {
            body.innerHTML =
              '<div class="ua-doc-email-container-fields">' +
              '<input class="form-control form-control-sm" data-field="background" placeholder="Background (surface/panel/raise/ink or #hex)" value="' + esc(data.background) + '">' +
              '<input class="form-control form-control-sm" data-field="align" placeholder="Align (left/center/right, optional)" value="' + esc(data.align) + '">' +
              '</div>';
          } else {
            body.innerHTML =
              '<div class="ua-doc-email-container-fields">' +
              '<input class="form-control form-control-sm" data-field="eyebrow" placeholder="Eyebrow (optional)" value="' + esc(data.eyebrow) + '">' +
              '<input class="form-control form-control-sm" data-field="title" placeholder="Title (optional)" value="' + esc(data.title) + '">' +
              '<input class="form-control form-control-sm" data-field="align" placeholder="Align (left/center/right, optional)" value="' + esc(data.align) + '">' +
              '</div>';
          }
          var fieldInputs = body.querySelectorAll("[data-field]");
          for (var fi = 0; fi < fieldInputs.length; fi++) { fieldInputs[fi].addEventListener("input", bubbleChange); }

          var childrenEl = document.createElement("div");
          childrenEl.className = "ua-doc-email-children";
          body.appendChild(childrenEl);
          // Nested add bar offers leaves only (one level of UI nesting).
          buildAddBar(body, addBlock, LEAF_TYPES, childrenEl);

          var childBlocks = (data.children instanceof Array) ? data.children : [];
          for (var ci = 0; ci < childBlocks.length; ci++) {
            var ct = childBlocks[ci] && childBlocks[ci].type;
            if (ct === "_raw" || LEAF_TYPES.indexOf(ct) !== -1 || isContainerType(ct)) {
              addBlock(ct, childBlocks[ci], childrenEl);
            }
          }

          parentEl.appendChild(row);
          refreshControls();
          bubbleChange();
          return;
        } else if (type === "greeting") {
          body.innerHTML =
            '<input class="form-control" data-field="name" placeholder="Name (e.g. ${name}, or leave blank for \'there\')" value="' + esc(data.name) + '">';
          body.querySelector("[data-field='name']").addEventListener("input", bubbleChange);
        } else if (type === "text") {
          var ed = document.createElement("div");
          ed.className = "ua-doc-email-text";
          body.appendChild(ed);
          var quill = new Quill(ed, {
            theme: state.theme,
            formats: EMAIL_SAFE_FORMATS,
            modules: { toolbar: [["bold", "italic", "underline"], ["link"], [{ list: "ordered" }, { list: "bullet" }]] }
          });
          addTokenMatcher(quill); // BEFORE hydrate so ${x} in data.html becomes chips
          if (data.html) { quill.clipboard.dangerouslyPasteHTML(data.html); }
          quill.on("text-change", bubbleChange);
          row.uaQuill = quill;
        } else if (type === "list") {
          body.innerHTML =
            '<label class="ua-doc-email-inline"><input type="checkbox" data-field="ordered"' + (data.ordered ? " checked" : "") + '> Numbered</label>' +
            '<textarea class="form-control" data-field="items" rows="4" placeholder="One item per line">' + esc((data.items || []).join("\n")) + '</textarea>';
          body.querySelector("[data-field='ordered']").addEventListener("change", bubbleChange);
          body.querySelector("[data-field='items']").addEventListener("input", bubbleChange);
        } else if (type === "button") {
          body.innerHTML =
            '<input class="form-control" data-field="label" placeholder="Button label" value="' + esc(data.label) + '">' +
            '<input class="form-control" data-field="url" placeholder="https://…" value="' + esc(data.url) + '">';
          body.querySelector("[data-field='label']").addEventListener("input", bubbleChange);
          body.querySelector("[data-field='url']").addEventListener("input", bubbleChange);
        } else if (type === "image") {
          body.innerHTML =
            '<input class="form-control" data-field="src" placeholder="Image URL (absolute)" value="' + esc(data.src) + '">' +
            '<input type="file" class="form-control" data-field="file" accept="image/*">' +
            '<input class="form-control" data-field="alt" placeholder="Alt text" value="' + esc(data.alt) + '">' +
            '<input class="form-control" data-field="width" placeholder="Width (px, max 480)" value="' + esc(data.width) + '">' +
            '<input class="form-control" data-field="href" placeholder="Link URL (optional)" value="' + esc(data.href) + '">';
          body.querySelector("[data-field='src']").addEventListener("input", bubbleChange);
          body.querySelector("[data-field='alt']").addEventListener("input", bubbleChange);
          body.querySelector("[data-field='width']").addEventListener("input", bubbleChange);
          body.querySelector("[data-field='href']").addEventListener("input", bubbleChange);
          body.querySelector("[data-field='file']").addEventListener("change", function () {
            uploadImage(this, body.querySelector("[data-field='src']"), state.uploadUrl, bubbleChange);
          });
        } else if (type === "spacer") {
          body.innerHTML =
            '<input class="form-control" data-field="height" placeholder="Height (px)" value="' + esc(data.height || 24) + '">';
          body.querySelector("[data-field='height']").addEventListener("input", bubbleChange);
        } else if (type === "divider") {
          body.innerHTML =
            '<input class="form-control" data-field="color" placeholder="Line color (hex, optional)" value="' + esc(data.color) + '">';
          body.querySelector("[data-field='color']").addEventListener("input", bubbleChange);
        }

        parentEl.appendChild(row);
        refreshControls();
        bubbleChange();
      }

      // Hydrate from the initial value (top-level rows; containers hydrate their own
      // children). `_raw` preserved regions are hydrated too so they round-trip.
      var initial = [];
      try { initial = JSON.parse(host.getAttribute("data-blocks") || "[]"); }
      catch (e) { initial = []; }
      if (!Array.isArray(initial)) { initial = []; }
      for (var b = 0; b < initial.length; b++) {
        var bt = initial[b] && initial[b].type;
        if (bt === "_raw" || BLOCK_TYPES.indexOf(bt) !== -1) {
          addBlock(bt, initial[b], blocksEl);
        }
      }

      buildAddBar(host, addBlock, BLOCK_TYPES, blocksEl);
      refreshControls();

      // Expose the reader used by save().
      state.read = function () { return readBlocks(blocksEl); };
    }
  } finally {
    isLoading = false;
  }
});

// Upload a chosen image file to the app route; on success set the src field to the
// returned ABSOLUTE URL. Browser only.
function uploadImage(fileInput, srcInput, uploadUrl, onDone) {
  if (!fileInput.files || !fileInput.files[0]) { return; }
  var fd = new FormData();
  fd.append("file", fileInput.files[0]);
  fetch(uploadUrl, { method: "POST", body: fd, credentials: "same-origin" })
    .then(function (r) { return r.json(); })
    .then(function (j) {
      if (j && j.success && j.url) {
        srcInput.value = j.url;
        if (onDone) { onDone(); }
      } else {
        console.error("email image upload failed", j);
      }
    })
    .catch(function (err) { console.error("email image upload error", err); });
}

// The DIRECT-child block rows of a blocks container (never nested descendants).
function directBlockRows(container) {
  var out = [];
  if (!container) { return out; }
  var kids = container.children;
  for (var i = 0; i < kids.length; i++) {
    if (kids[i].classList && kids[i].classList.contains("ua-doc-email-block")) { out.push(kids[i]); }
  }
  return out;
}

// A row's OWN body (its direct `.ua-doc-email-body` child).
function directBody(row) {
  if (!row) { return null; }
  var kids = row.children;
  for (var i = 0; i < kids.length; i++) {
    if (kids[i].classList && kids[i].classList.contains("ua-doc-email-body")) { return kids[i]; }
  }
  return null;
}

// A container's OWN attribute inputs (from its `.ua-doc-email-container-fields`, never
// a nested child's inputs).
function directFieldInputs(body) {
  var fieldsWrap = body ? body.querySelector(".ua-doc-email-container-fields") : null;
  if (!fieldsWrap) { return []; }
  var found = fieldsWrap.querySelectorAll("[data-field]");
  return Array.prototype.slice.call(found);
}

// Read the current DOM back into the block model array. Browser only. Recurses into
// container children. `blocksEl` is a blocks container; only its DIRECT child rows are
// read (a container reads its own `.ua-doc-email-children`).
function readBlocks(blocksEl) {
  var out = [];
  var rows = directBlockRows(blocksEl);
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i];
    var type = row.getAttribute("data-type");
    var body = directBody(row);
    if (type === "_raw") {
      // Re-emit the preserved node verbatim.
      out.push({ type: "_raw", node: row.uaRaw || null });
    } else if (type === "card" || type === "section") {
      var childrenEl = body ? body.querySelector(".ua-doc-email-children") : null;
      var block = { type: type };
      var fields = body ? directFieldInputs(body) : [];
      for (var f = 0; f < fields.length; f++) {
        var fname = fields[f].getAttribute("data-field");
        var fval = fields[f].value != null ? fields[f].value.trim() : "";
        if (fval !== "") { block[fname] = fval; }
      }
      block.children = childrenEl ? readBlocks(childrenEl) : [];
      out.push(block);
    } else if (type === "greeting") {
      var gname = body ? body.querySelector("[data-field='name']") : null;
      var g = { type: "greeting" };
      var gv = gname && gname.value != null ? gname.value.trim() : "";
      if (gv !== "") { g.name = gv; }
      out.push(g);
    } else if (type === "text") {
      var quill = row.uaQuill;
      var html = quill ? sanitizeEmailHtml(quill.getSemanticHTML()) : "";
      out.push({ type: "text", html: html });
    } else if (type === "list") {
      var ordered = !!body.querySelector("[data-field='ordered']").checked;
      var raw = body.querySelector("[data-field='items']").value || "";
      var items = raw.split("\n").map(function (s) { return s.trim(); }).filter(function (s) { return s !== ""; });
      out.push({ type: "list", ordered: ordered, items: items });
    } else if (type === "button") {
      out.push({
        type: "button",
        label: body.querySelector("[data-field='label']").value || "",
        url: body.querySelector("[data-field='url']").value || ""
      });
    } else if (type === "image") {
      var w = parseInt(body.querySelector("[data-field='width']").value, 10);
      out.push({
        type: "image",
        src: body.querySelector("[data-field='src']").value || "",
        alt: body.querySelector("[data-field='alt']").value || "",
        width: (w > 0) ? w : undefined,
        href: body.querySelector("[data-field='href']").value || ""
      });
    } else if (type === "spacer") {
      var h = parseInt(body.querySelector("[data-field='height']").value, 10);
      out.push({ type: "spacer", height: (h > 0) ? h : 24 });
    } else if (type === "divider") {
      var color = body.querySelector("[data-field='color']").value || "";
      var d = { type: "divider" };
      if (color) { d.color = color; }
      out.push(d);
    }
  }
  return out;
}

// A ${identifier} occurrence in a string. Global — a rich-text block can hold several.
var TOKEN_RE = /\$\{[A-Za-z0-9_]+\}/g;

// Parse a control's declared token list — accepts a JSON array or a comma/space
// separated list of ${…} tokens. The APP supplies these (the framework stays
// tenant-neutral); an absent/blank attribute means "no token requirement".
function parseTokenList(raw) {
  var s = (raw == null ? "" : String(raw)).trim();
  if (!s) { return []; }
  if (s.charAt(0) === "[") {
    try { var arr = JSON.parse(s); return Array.isArray(arr) ? arr.map(String) : []; }
    catch (e) { /* fall through to delimiter parse */ }
  }
  return s.split(/[\s,]+/).filter(function (t) { return t !== ""; });
}

// Every ${token} present anywhere in a block array (deep-walks strings, incl. nested
// container children and preserved `_raw` nodes) — the same shape the server validator
// sees, so client + server agree on what "present" means.
function collectBlockTokens(blocks) {
  var seen = {};
  (function walk(v) {
    if (v == null) { return; }
    if (typeof v === "string") {
      var m = v.match(TOKEN_RE);
      if (m) { for (var i = 0; i < m.length; i++) { seen[m[i]] = true; } }
      return;
    }
    if (Array.isArray(v)) { for (var j = 0; j < v.length; j++) { walk(v[j]); } return; }
    if (typeof v === "object") { for (var k in v) { if (Object.prototype.hasOwnProperty.call(v, k)) { walk(v[k]); } } }
  })(blocks);
  return Object.keys(seen);
}

/**
 * Validate the control's current block state against required/min/max.
 * @param {*} control The uae placeholder element.
 * @param {*} name The field name.
 * @returns {Array} errors
 */
function validation(control, name) {
  var errors = [];
  control.classList.remove("is-invalid");
  control.classList.remove("is-valid");

  var state = control.uaDocEmail;
  var blocks = (state && state.read) ? state.read() : [];

  if (control.hasAttribute("required") && control.getAttribute("required") === "true") {
    if (blocks.length === 0) {
      errors.push({ type: "validation", message: "Add at least one content block.", input: { id: name, name: name } });
    }
  }
  if (control.hasAttribute("minlength")) {
    var min = parseInt(control.getAttribute("minlength"), 10);
    if (min > 0 && blocks.length < min) {
      errors.push({ type: "validation", message: `Add at least ${min} blocks.`, input: { id: name, name: name } });
    }
  }
  if (control.hasAttribute("maxlength")) {
    var max = parseInt(control.getAttribute("maxlength"), 10);
    if (max > 0 && blocks.length > max) {
      errors.push({ type: "validation", message: `Use at most ${max} blocks.`, input: { id: name, name: name } });
    }
  }

  // REQUIRED-TOKEN surface (M4/EE5). The app declares the ${…} tokens this control must
  // retain via a `required-tokens` attribute (JSON array or comma/space list) — the
  // framework hardcodes no tenant token. A dropped required token is the founder's #1
  // fear (a silent ${code} loss). This is the CLIENT mirror; the authoritative fail-
  // closed refusal is server-side in app/models/egt.email.validate.js. The live UI
  // display of these is founder-gated — this only wires the structure.
  var requiredTokens = parseTokenList(
    control.getAttribute("required-tokens") || control.getAttribute("data-required-tokens")
  );
  if (requiredTokens.length) {
    var present = collectBlockTokens(blocks);
    for (var t = 0; t < requiredTokens.length; t++) {
      if (present.indexOf(requiredTokens[t]) === -1) {
        errors.push({
          type: "validation",
          message: `Required token ${requiredTokens[t]} is missing — it cannot be removed.`,
          input: { id: name, name: name }
        });
      }
    }
  }
  return errors;
}

/**
 * Saves the block model from the control. Called by UATools.
 * @param {*} name The name of the control.
 * @param {*} control The control (uae placeholder).
 * @param {*} repo The prevalidation repo.
 * @returns {*} repo with data = block array.
 */
async function save(name, control, repo) {
  repo.errors = validation(control, name);
  if (repo.errors.length > 0) {
    repo.success = false;
    control.classList.add("is-invalid");
  } else {
    repo.success = true;
    control.classList.add("is-valid");
  }

  var state = control.uaDocEmail;
  repo.data = (state && state.read) ? state.read() : [];
  return repo;
} module.exports.save = save;

/**
 * Loads the control with a block array (or a { blocks:[...] } body).
 * @param {*} name The name of the control.
 * @param {*} control The control (uae placeholder).
 * @param {*} data The block array or body object.
 */
async function load(name, control, data) {
  if (!data) { return; }

  var blocks = [];
  if (Array.isArray(data)) { blocks = data; }
  else if (typeof data === "string") {
    try { var p = JSON.parse(data); if (Array.isArray(p)) { blocks = p; } } catch (e) { blocks = []; }
  } else if (typeof data === "object" && Array.isArray(data.blocks)) {
    blocks = data.blocks;
  }

  var state = control.uaDocEmail;
  var host = control.querySelector(".ua-doc-email");
  if (!state || !host) {
    // Not yet initialized — stash for afterrender to hydrate from.
    if (host) { host.setAttribute("data-blocks", JSON.stringify(blocks)); }
    return;
  }

  // Rebuild: clear existing block rows, then re-add from data.
  var blocksEl = host.querySelector(".ua-doc-email-blocks");
  if (blocksEl) {
    while (blocksEl.firstChild) { blocksEl.removeChild(blocksEl.firstChild); }
  }
  host.setAttribute("data-blocks", JSON.stringify(blocks));
  // Re-trigger the initializer path by clearing the init flag and firing render.
  control.uaDocEmail = null;
  events.emit("interface.afterrender");
} module.exports.load = load;
